import { Injectable, inject } from '@angular/core';
import { GetFollowUpsListAction } from '@app/modules/follow-ups/store/follow-ups.actions';
import { FollowUpsState } from '@app/modules/follow-ups/store/follow-ups.state';
import { KitsUtilsService } from '@app/modules/kits/kits-utils.service';
import { SampleDialogComponent } from '@app/modules/service-data/components/sample/sample-dialog/sample-dialog.component';
import { SampleKitChangeDialogComponent } from '@app/modules/service-data/components/sample/sample-kit-change-dialog/sample-kit-change-dialog.component';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import {
  GetServiceDataAction,
  GetServiceDataListAction,
} from '@app/modules/service-data/store/service-data.actions';
import { ServiceDataState } from '@app/modules/service-data/store/service-data.state';
import {
  ExtendedSample,
  ProvisionSample,
  Sample,
  SampleExtensions,
  SampleType,
} from '@app/modules/service-data/types/sample.types';
import {
  ExportDialogDataType,
  ExportType,
  ServiceData,
} from '@app/modules/service-data/types/service-data.types';
import { DataInputType, RuntimeStatus } from '@core/core.types';
import { ExportersUtilsService } from '@core/services/exporters-utils.service';
import { LoadKitById, LoadKitsByIdAction } from '@core/store/kits/kits.actions';
import { KitsState } from '@core/store/kits/kits.state';
import {
  LoadPartnerById,
  LoadPartnersByIds,
} from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import { ProductsListState } from '@core/store/products/products.state';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import moment from 'moment';
import { Observable, combineLatest, forkJoin } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { SampleDetailsComponent } from '../components/sample/sample-details/sample-details.component';
import { SampleResultsComponent } from '../components/sample/sample-results/sample-results.component';
import { SampleFilters } from './sample.service';

@Injectable({
  providedIn: 'root',
})
export class SampleUtilsService {
  private store = inject(Store);
  private serviceDataUtils = inject(ServiceDataUtilsService);
  private exportersUtilsService = inject(ExportersUtilsService);
  private translate = inject(TranslateService);
  private ngbModal = inject(NgbModal);
  private kitsUtils = inject(KitsUtilsService);

  getExtendedSample(
    sample: Sample,
    appends: SampleExtensions = {
      partner: true,
      product: true,
      doctor: true,
      serviceData: null,
      kit: null,
      followUps: false,
    }
  ): Observable<ExtendedSample> {
    if (!sample) {
      return of(null);
    }
    let doctorId = this.serviceDataUtils.getPropertyFromServiceInformation(
      sample?.serviceInformation,
      'doctorId'
    );

    return forkJoin([
      appends.partner
        ? this.store.dispatch(new LoadPartnerById(sample?.partnerId))
        : of(null),
      appends.doctor
        ? this.store.dispatch(new LoadPartnerById(doctorId))
        : of(null),
      appends.serviceData
        ? this.store.dispatch(new GetServiceDataAction(sample?.serviceDataId))
        : of(null),
      appends.kit
        ? this.store.dispatch(new LoadKitById(sample?.kitId))
        : of(null),
      appends.followUps
        ? this.store.dispatch(
            new GetFollowUpsListAction(
              { ids: sample.followUpIds },
              null,
              null,
              null
            )
          )
        : of(null),
    ]).pipe(
      switchMap(() =>
        combineLatest([
          of(sample),
          this.store
            .select(ProductsListState.getProduct)
            .pipe(map((getProduct) => getProduct(sample?.productId))),
          this.store
            .select(PartnersState.getPartnerById)
            .pipe(map((getPartnerById) => getPartnerById(sample?.partnerId))),
          this.store
            .select(PartnersState.getPartnerById)
            .pipe(map((getPartnerById) => getPartnerById(doctorId))),
          appends.serviceData
            ? this.store
                .select(ServiceDataState.getServiceDataById)
                .pipe(
                  first(),
                  map((getServiceDataById) =>
                    getServiceDataById(sample?.serviceDataId)
                  )
                )
                .pipe(
                  switchMap((serviceData) =>
                    this.serviceDataUtils.getExtendedServiceData(
                      serviceData,
                      appends.serviceData
                    )
                  )
                )
            : of(null),
          appends.kit
            ? this.store
                .select(KitsState.getKitById)
                .pipe(map((getKitById) => getKitById(sample?.kitId)))
                .pipe(
                  switchMap((kit) =>
                    this.kitsUtils.getExtendedKit(kit, appends.kit)
                  )
                )
            : of(null),
          this.store
            .select(FollowUpsState.getFollowUpsByIds)
            .pipe(
              map((getFollowUpsByIds) => getFollowUpsByIds(sample?.followUpIds))
            ),
        ])
      ),
      map(
        ([sample, product, partner, doctor, serviceData, kit, followUps]) => ({
          ...sample,
          product,
          partner,
          doctor,
          serviceData,
          kit,
          followUps,
        })
      )
    );
  }

  getExtendedSampleList(
    sampleList: Sample[],
    appends: SampleExtensions
  ): Observable<ExtendedSample[]> {
    if (!sampleList.length) {
      return of([]);
    }

    return this.loadSampleConnectedData(sampleList, appends).pipe(
      switchMap(() =>
        combineLatest(
          sampleList.map((sample) => this.getExtendedSample(sample, appends))
        )
      )
    );
  }

  loadSampleConnectedData(sampleList: Sample[], appends: SampleExtensions) {
    if (!sampleList.length) {
      return of([]);
    }

    const productIds: string[] = [];
    const kitIds: string[] = [];
    const partnerIds: string[] = [];
    const serviceDataIds: string[] = [];
    let followUpIds: string[] = [];
    sampleList.forEach((sample) => {
      let doctorId = this.serviceDataUtils.getPropertyFromServiceInformation(
        sample?.serviceInformation,
        'doctorId'
      );
      productIds.push(sample?.productId);
      if (appends.partner) {
        partnerIds.push(sample?.partnerId);
      }
      if (appends.doctor) {
        partnerIds.push(doctorId);
      }
      if (appends.followUps) {
        followUpIds = [...followUpIds, ...(sample?.followUpIds ?? [])];
      }
      kitIds.push(sample?.kitId);
      serviceDataIds.push(sample?.serviceDataId);
    });

    return forkJoin([
      appends.partner || appends.doctor
        ? this.store.dispatch(new LoadPartnersByIds(partnerIds))
        : of(null),
      appends.serviceData
        ? this.store
            .dispatch(
              new GetServiceDataListAction(
                { ids: serviceDataIds },
                {},
                null,
                false
              )
            )
            .pipe(
              switchMap(() =>
                this.store
                  .select(ServiceDataState.getServiceDatasByIds)
                  .pipe(map((getByIds) => getByIds(serviceDataIds)))
              ),
              first(),
              switchMap((serviceDatas) =>
                this.serviceDataUtils.loadServiceDataConnectedData(
                  serviceDatas,
                  appends.serviceData
                )
              )
            )
        : of(null),
      appends.kit
        ? this.store.dispatch(new LoadKitsByIdAction(kitIds)).pipe(
            switchMap(() =>
              this.store
                .select(KitsState.getKitsByIds)
                .pipe(map((getKitsByIds) => getKitsByIds(kitIds)))
            ),
            first(),
            switchMap((kits) =>
              this.kitsUtils.loadKitConnectedData(kits, appends.kit)
            )
          )
        : of(null),
      appends.followUps
        ? this.store.dispatch(
            new GetFollowUpsListAction({ ids: followUpIds }, {}, null, false)
          )
        : of(null),
    ]);
  }

  public openDetails(sampleId: string) {
    const modalRef = this.ngbModal.open(SampleDetailsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-xxl',
    });

    modalRef.componentInstance.sampleId = sampleId;
  }

  public openEdit(sampleId: string, serviceDataId: string) {
    const modalRef = this.ngbModal.open(SampleDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-lg',
    });

    modalRef.componentInstance.sampleId = sampleId;
    modalRef.componentInstance.serviceDataId = serviceDataId;
  }

  public openResample(sampleId: string, serviceDataId: string, resampleKitId) {
    const modalRef = this.ngbModal.open(SampleDialogComponent, {
      centered: true,
      container: 'body',
      modalDialogClass: 'modal-size-lg',
    });

    modalRef.componentInstance.sampleId = sampleId;
    modalRef.componentInstance.serviceDataId = serviceDataId;
    modalRef.componentInstance.resampleKitId = resampleKitId;
  }

  public replaceKit(sampleId: string) {
    const modalRef = this.ngbModal.open(SampleKitChangeDialogComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.sampleId = sampleId;
  }

  buildHttpParams(params: SampleFilters) {
    let httpParams = HttpUtils.buildHttpParams(params);

    if (params.kitIds?.length) {
      httpParams = httpParams.set('kitIds', params.kitIds.join(','));
    }

    if (params.serviceDataPartnerIds?.length) {
      httpParams = httpParams.set(
        'serviceDataPartnerIds',
        params.serviceDataPartnerIds.join(',')
      );
    }

    if (params.laboratories?.length) {
      httpParams = httpParams.set(
        'laboratories',
        params.laboratories.join(',')
      );
    }

    if (params.productIds?.length) {
      httpParams = httpParams.set('productIds', params.productIds.join(','));
    }

    if (params.accountManagerId?.length) {
      httpParams = httpParams.set(
        'accountManagerId',
        params.accountManagerId.join(',')
      );
    }

    return httpParams;
  }

  public openResults(sampleIds: string[]) {
    const modalRef = this.ngbModal.open(SampleResultsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.sampleIds = sampleIds;
  }

  public exportSelected(
    exportType: ExportType,
    ids?: string[]
  ): Observable<string> {
    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translate.instant(
        'sample.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      )
    );

    return this.exportersUtilsService
      .exportSelected(ExportDialogDataType.Sample, ids, exportType)
      .pipe(
        tap((status: string) => {
          if (status !== RuntimeStatus.Running) {
            progressSubject.next({
              value: 100,
              status: 'shared.progress.done',
              success: status === RuntimeStatus.Completed ? true : false,
            });
          }
        })
      );
  }

  public export(
    exportType: ExportType,
    filters?: { [key: string]: string }
  ): Observable<string> {
    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translate.instant(
        'sample.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      )
    );

    return this.exportersUtilsService.export(filters, exportType).pipe(
      tap((status: string) => {
        if (status !== RuntimeStatus.Running) {
          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      })
    );
  }

  public formToSample(
    formData: any,
    inputTypes: DataInputType[]
  ): ProvisionSample {
    const sample: ProvisionSample = {
      dateOfVenipuncture: formData.dateOfVenipuncture,
      productId: formData.productId,
      serviceDataId: formData.serviceDataId,
      shippingInformation: formData.shippingInformation,
      id: formData.id,
      kitId: formData.kitId,
      partnerId: formData.partnerId,
      notes: formData.notes,
      serviceInformation: {},
    };

    if (inputTypes.includes(DataInputType.NIPT)) {
      sample.serviceInformation.nipt = {
        gestationAge: formData.serviceInformation.shared.gestationAge,
        isGestationAgeAutomaticallyCalculated:
          formData.serviceInformation.shared
            .isGestationAgeAutomaticallyCalculated,
        priorScreeningTest: formData.serviceInformation.nipt.priorScreeningTest,
        ultrasoundDate: formData.serviceInformation.shared.ultrasoundDate,
        doctorId: formData.serviceInformation.shared.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.NIPPT)) {
      sample.serviceInformation.nippt = {
        doctorId: formData.serviceInformation.shared.doctorId,
        ...this.sampleTypeFromForm(formData),
      };
    }

    if (inputTypes.includes(DataInputType.PATERNITY)) {
      sample.serviceInformation.paternity = {
        doctorId: formData.serviceInformation.shared.doctorId,
        participantId: formData.serviceInformation.shared.participantId,
        ...this.sampleTypeFromForm(formData),
      };
    }

    if (inputTypes.includes(DataInputType.MONO)) {
      sample.serviceInformation.mono = {
        gestationAge: formData.serviceInformation.shared.gestationAge,
        isGestationAgeAutomaticallyCalculated:
          formData.serviceInformation.shared
            .isGestationAgeAutomaticallyCalculated,
        ultrasoundDate: formData.serviceInformation.shared.ultrasoundDate,
        doctorId: formData.serviceInformation.shared.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.RH)) {
      sample.serviceInformation.rhd = {
        gestationAge: formData.serviceInformation.shared.gestationAge,
        isGestationAgeAutomaticallyCalculated:
          formData.serviceInformation.shared
            .isGestationAgeAutomaticallyCalculated,
        doctorId: formData.serviceInformation.shared.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.CARRIER)) {
      sample.serviceInformation.carrier = {
        doctorId: formData.serviceInformation.shared.doctorId,
      };
    }

    if (
      inputTypes.includes(DataInputType.VENISAFE) ||
      inputTypes.includes(DataInputType.VENISAFEMATERNA)
    ) {
      sample.serviceInformation.venisafe = {
        doctorId: formData.serviceInformation.shared.doctorId,
        sampleType: formData.serviceInformation.shared.sampleType,
      };
    }

    if (inputTypes.includes(DataInputType.CANCERSCREEN)) {
      sample.serviceInformation.cancerScreen = {
        doctorId: formData.serviceInformation.shared.doctorId,
        sampleType: formData.serviceInformation.shared.sampleType,
      };
    }

    if (inputTypes.includes(DataInputType.GALLERI)) {
      sample.serviceInformation.galleri = {
        doctorId: formData.serviceInformation.shared.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.NEGSINGLECARRIER)) {
      sample.serviceInformation.negSingleCarrier = {
        doctorId: formData.serviceInformation.shared.doctorId,
        ...this.sampleTypeFromForm(formData),
      };
    }

    if (inputTypes.includes(DataInputType.OTHER)) {
      sample.serviceInformation.other = {
        doctorId: formData.serviceInformation.shared.doctorId,
      };
    }

    return sample;
  }

  // NIPPT, Paternity: with "Other" the API gets sampleType "Other" and the
  // typed-in type as otherType
  private sampleTypeFromForm(formData: any) {
    const sampleType: string = formData.serviceInformation.shared.sampleType;
    const isOther = sampleType === SampleType.Other;

    return {
      sampleType,
      otherType: isOther
        ? (formData.serviceInformation.shared.otherType ?? '').trim()
        : null,
    };
  }

  private sampleTypeToForm(sample: {
    sampleType: string;
    otherType: string | null;
  }) {
    return {
      sampleType: sample?.otherType ? SampleType.Other : sample?.sampleType,
      otherType: sample?.otherType ?? null,
    };
  }

  sampleToForm(sample: Sample, inputTypes: DataInputType[]) {
    const formData = {
      ...sample,
      serviceInformation: {
        shared: {
          currentPregnancyInformation: {},
          patientHistory: {},
        },
      },
    } as any;

    if (inputTypes.includes(DataInputType.NIPT)) {
      formData.serviceInformation.nipt = {
        priorScreeningTest: sample.serviceInformation.nipt.priorScreeningTest,
      };
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId: sample.serviceInformation.nipt.doctorId,
        gestationAge: sample.serviceInformation.nipt.gestationAge,
        ultrasoundDate: sample.serviceInformation.nipt.ultrasoundDate,
      };
    }

    if (inputTypes.includes(DataInputType.NIPPT)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId: sample.serviceInformation.nippt.doctorId,
        ...this.sampleTypeToForm(sample.serviceInformation.nippt),
      };
    }

    if (inputTypes.includes(DataInputType.PATERNITY)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId: sample.serviceInformation.paternity.doctorId,
        participantId: sample.serviceInformation.paternity.participantId,
        ...this.sampleTypeToForm(sample.serviceInformation.paternity),
      };
    }

    if (inputTypes.includes(DataInputType.MONO)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        gestationAge: sample.serviceInformation.mono.gestationAge,
        ultrasoundDate: sample.serviceInformation.mono.ultrasoundDate,
        doctorId: sample.serviceInformation.mono.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.RH)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        gestationAge: sample.serviceInformation.rhd.gestationAge,
        doctorId: sample.serviceInformation.rhd.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.CARRIER)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId: sample.serviceInformation.carrier.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.CANCERSCREEN)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        sampleType: sample.serviceInformation.cancerScreen.sampleType,
        doctorId: sample.serviceInformation.cancerScreen.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.GALLERI)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId: sample.serviceInformation.galleri.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.NEGSINGLECARRIER)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId: sample.serviceInformation.negSingleCarrier.doctorId,
        ...this.sampleTypeToForm(sample.serviceInformation.negSingleCarrier),
      };
    }

    if (
      inputTypes.includes(DataInputType.VENISAFE) ||
      inputTypes.includes(DataInputType.VENISAFEMATERNA)
    ) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        sampleType: sample.serviceInformation.venisafe.sampleType,
        doctorId: sample.serviceInformation.venisafe.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.OTHER)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId: sample.serviceInformation.other.doctorId,
      };
    }

    formData.serviceInformation.shared.ultrasoundDate = formData
      .serviceInformation?.shared?.ultrasoundDate
      ? moment(formData.serviceInformation?.shared?.ultrasoundDate)
      : null;

    formData.dateOfVenipuncture = formData.dateOfVenipuncture
      ? moment(formData.dateOfVenipuncture)
      : null;

    return formData;
  }
  serviceDataToSampleForm(
    serviceData: ServiceData,
    sampleId: string,
    resampleKitId: string,
    inputTypes: DataInputType[]
  ) {
    const formData = {
      partnerId: serviceData.partnerId,
      dateOfVenipuncture: moment(),
      serviceDataId: serviceData.id,
      serviceInformation: {
        shared: {
          currentPregnancyInformation: {},
          patientHistory: {},
        },
      },
    } as any;

    if (inputTypes.includes(DataInputType.NIPT)) {
      formData.serviceInformation.nipt = {
        priorScreeningTest:
          serviceData.serviceInformation.nipt.currentPregnancyInformation
            .priorScreeningTest,
      };
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId:
          serviceData.serviceInformation.nipt.doctorInformation.doctorId,
        gestationAge:
          serviceData.serviceInformation.nipt.currentPregnancyInformation
            .gestationAge,
        ultrasoundDate:
          serviceData.serviceInformation.nipt.currentPregnancyInformation
            .ultrasoundDate,
        patientHistory: serviceData.serviceInformation.nipt.patientHistory,
        heparinTherapy:
          serviceData.serviceInformation.nipt.currentPregnancyInformation
            .heparinTherapy,
        isGestationAgeAutomaticallyCalculated:
          sampleId && !resampleKitId
            ? false
            : serviceData.serviceInformation.nipt.currentPregnancyInformation
                .isGestationAgeAutomaticallyCalculated,
      };
    }

    if (inputTypes.includes(DataInputType.NIPPT)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId:
          serviceData.serviceInformation.nippt.doctorInformation.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.PATERNITY)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId:
          serviceData.serviceInformation.paternity.doctorInformation.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.MONO)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        gestationAge:
          serviceData.serviceInformation.mono.currentPregnancyInformation
            .gestationAge,
        ultrasoundDate:
          serviceData.serviceInformation.mono.currentPregnancyInformation
            .ultrasoundDate,
        doctorId:
          serviceData.serviceInformation.mono.doctorInformation.doctorId,
        patientHistory: {
          ...formData.serviceInformation.shared.patientHistory,
          ...serviceData.serviceInformation.mono.patientHistory,
        },
        heparinTherapy:
          serviceData.serviceInformation.mono.currentPregnancyInformation
            .heparinTherapy,
        isGestationAgeAutomaticallyCalculated:
          sampleId && !resampleKitId
            ? false
            : serviceData.serviceInformation.mono.currentPregnancyInformation
                .isGestationAgeAutomaticallyCalculated,
      };
    }

    if (inputTypes.includes(DataInputType.RH)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        gestationAge:
          serviceData.serviceInformation.rhd.currentPregnancyInformation
            .gestationAge,
        doctorId: serviceData.serviceInformation.rhd.doctorInformation.doctorId,
        patientHistory: {
          ...formData.serviceInformation.shared.patientHistory,
          ...serviceData.serviceInformation.rhd.patientHistory,
        },
        isGestationAgeAutomaticallyCalculated:
          sampleId && !resampleKitId
            ? false
            : serviceData.serviceInformation.rhd.currentPregnancyInformation
                .isGestationAgeAutomaticallyCalculated,
      };
    }

    if (inputTypes.includes(DataInputType.CARRIER)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId:
          serviceData.serviceInformation.carrier.doctorInformation.doctorId,
        patientHistory: {
          ...formData.serviceInformation.shared.patientHistory,
          ...serviceData.serviceInformation.carrier.patientHistory,
        },
      };
    }

    if (inputTypes.includes(DataInputType.CANCERSCREEN)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        sampleType: serviceData.serviceInformation.cancerScreen.sampleType,
        doctorId:
          serviceData.serviceInformation.cancerScreen.doctorInformation
            .doctorId,
        patientHistory: {
          ...formData.serviceInformation.shared.patientHistory,
          ...serviceData.serviceInformation.cancerScreen.patientHistory,
        },
      };
    }

    if (inputTypes.includes(DataInputType.GALLERI)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId:
          serviceData.serviceInformation.galleri.doctorInformation.doctorId,
      };
    }

    if (inputTypes.includes(DataInputType.NEGSINGLECARRIER)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId:
          serviceData.serviceInformation.negSingleCarrier.doctorInformation
            .doctorId,
      };
    }

    if (
      inputTypes.includes(DataInputType.VENISAFE) ||
      inputTypes.includes(DataInputType.VENISAFEMATERNA)
    ) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        sampleType: serviceData.serviceInformation.venisafe.sampleType,
        doctorId:
          serviceData.serviceInformation.venisafe.doctorInformation.doctorId,
        patientHistory: {
          ...formData.serviceInformation.shared.patientHistory,
          ...serviceData.serviceInformation.venisafe.patientHistory,
        },
      };
    }

    if (inputTypes.includes(DataInputType.OTHER)) {
      formData.serviceInformation.shared = {
        ...formData.serviceInformation.shared,
        doctorId:
          serviceData.serviceInformation.other.doctorInformation.doctorId,
      };
    }

    formData.serviceInformation.shared.ultrasoundDate = formData
      .serviceInformation?.shared?.ultrasoundDate
      ? moment(formData.serviceInformation?.shared?.ultrasoundDate)
      : null;

    return formData;
  }
}
